import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth/useAuth";
import { createApiClient, type Widget, type ContextField, type BulkAssignResult } from "@/lib/api/chatApi";
import { filterUsers, totalPages as computeTotalPages, pageSlice, toggleInSet, toggleAll, selectAll, parseEmails } from "@/lib/userTable";
import { agentChipClass } from "@/lib/agentColors";
import Header from "@/components/layout/Header";
import Pestanas from "@/components/admin/Pestanas";
import SeccionModelos from "@/components/admin/SeccionModelos";
import PermisosPorAgente from "@/components/admin/PermisosPorAgente";
import { useVistaPermisos, VISTAS_PERMISOS } from "@/components/admin/vistaPermisos";
import { usuariosPorAgente } from "@/lib/permisosPorAgente";
import { usePestanaAdmin, idPestana, idPanel } from "@/components/admin/pestanasEstado";
import { Pencil, Trash2, Plus, ArrowLeft, Search, RefreshCw, Code, AlertTriangle, Users } from "lucide-react";

interface UserEntry {
  email: string;
  allowed_agents: string[];
  created_at: string | null;
  updated_at: string | null;
}

export default function AdminPage() {
  const { t } = useTranslation();
  const { user, getToken, logout } = useAuth();
  const navigate = useNavigate();
  const api = useMemo(() => createApiClient(getToken, logout), [getToken, logout]);

  // Pestana activa. El ESTADO de las tres secciones se queda aqui, en el padre:
  // cambiar de pestana solo deja de PINTAR una seccion, no la desmonta
  // logicamente, asi que lo que el admin llevara escrito (busqueda, pagina,
  // seleccion masiva) sigue ahi al volver.
  const [pestana, setPestana] = usePestanaAdmin();
  const [vistaPermisos, setVistaPermisos] = useVistaPermisos();
  // En el padre por lo mismo que la busqueda: cambiar de pestaña no puede
  // plegar lo que el admin tenia abierto.
  const [agentesAbiertos, setAgentesAbiertos] = useState<Set<string>>(new Set());

  const [users, setUsers] = useState<UserEntry[]>([]);
  const [agents, setAgents] = useState<{ graph_id: string; description: string; ui_enabled: boolean; hitl?: boolean; context_fields?: ContextField[]; context_prompt_template?: string | null }[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editAgents, setEditAgents] = useState<string[]>([]);
  const [isNew, setIsNew] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAgent, setBulkAgent] = useState<string>("");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkModalAgents, setBulkModalAgents] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<{ updated: number; invalid: string[] } | null>(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<{
    id: number; client_name: string; key_id: string; allowed_origins: string[];
    guardrails: boolean; active: boolean; created_at: string | null; last_used_at: string | null;
  }[]>([]);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [keyIsNew, setKeyIsNew] = useState(true);
  const [keyEditId, setKeyEditId] = useState<number | null>(null);
  const [keyName, setKeyName] = useState("");
  const [keyOrigins, setKeyOrigins] = useState("");
  const [keyGuardrails, setKeyGuardrails] = useState(false);
  const [secretDialog, setSecretDialog] = useState<{ keyId: string; secret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Widgets state
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);
  const [widgetForm, setWidgetForm] = useState<{
    name: string; agent_id: string; context_schema: ContextField[];
    context_prompt_template: string; thread_ttl_hours: number; allowed_origins: string;
    auto_start: boolean;
  }>({ name: "", agent_id: "", context_schema: [], context_prompt_template: "", thread_ttl_hours: 24, allowed_origins: "", auto_start: false });
  const [snippetHtml, setSnippetHtml] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [showSnippetModal, setShowSnippetModal] = useState(false);
  const [snippetCopied, setSnippetCopied] = useState(false);

  const fetched = useRef(false);
  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    if (fetched.current) return;
    fetched.current = true;
    Promise.all([
      api.listUsers().then(setUsers),
      api.listAssistants(false).then(setAgents),
      api.listApiKeys().then(setApiKeys),
      api.listWidgets().then(setWidgets),
    ]).catch(console.error);
  }, [api, user, navigate]);

  const openAdd = () => {
    setEditEmail("");
    setEditAgents([]);
    setIsNew(true);
    setModalOpen(true);
  };

  const openEdit = (u: UserEntry) => {
    setEditEmail(u.email);
    setEditAgents([...u.allowed_agents]);
    setIsNew(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    await api.upsertUser(editEmail, editAgents);
    setModalOpen(false);
    const updated = await api.listUsers();
    setUsers(updated);
  };

  const handleDelete = async (email: string) => {
    if (!confirm(t("admin.permissions.deleteConfirm", { email }))) return;
    await api.deleteUser(email);
    setUsers(users.filter((u) => u.email !== email));
  };

  const toggleAgent = (agentId: string) => {
    setEditAgents((prev) =>
      prev.includes(agentId) ? prev.filter((a) => a !== agentId) : [...prev, agentId],
    );
  };

  const toggleSelected = (email: string) => {
    setSelected((prev) => toggleInSet(prev, email));
  };

  const toggleSelectAllPage = () => {
    setSelected((prev) => toggleAll(prev, pageUsers.map((u) => u.email)));
  };

  const handleBulkSelected = async (op: "add" | "remove") => {
    const emails = [...selected];
    const opLabel = op === "add"
      ? t("admin.permissions.bulk.opAdd")
      : t("admin.permissions.bulk.opRemove");
    if (!confirm(t("admin.permissions.bulk.confirmApply", { op: opLabel, agent: bulkAgent, count: emails.length }))) return;
    await api.bulkAssignUsers({ emails, agents: [bulkAgent], op, validate: false });
    setSelected(new Set());
    setBulkAgent("");
    setUsers(await api.listUsers());
  };

  const openBulkModal = () => {
    setBulkEmails("");
    setBulkModalAgents([]);
    setBulkResult(null);
    setBulkModalOpen(true);
  };

  const toggleBulkModalAgent = (agentId: string) => {
    setBulkModalAgents((prev) =>
      prev.includes(agentId) ? prev.filter((a) => a !== agentId) : [...prev, agentId],
    );
  };

  const handleBulkByEmails = async () => {
    const emails = parseEmails(bulkEmails);
    const res: BulkAssignResult = await api.bulkAssignUsers({ emails, agents: bulkModalAgents, op: "add", validate: true });
    setBulkResult({ updated: res.updated.length, invalid: res.invalid });
    if (res.invalid.length === 0) {
      setUsers(await api.listUsers());
    }
  };

  // API Keys handlers
  const openCreateKey = () => {
    setKeyName(""); setKeyOrigins(""); setKeyGuardrails(false);
    setKeyIsNew(true); setKeyEditId(null); setKeyModalOpen(true);
  };

  const openEditKey = (k: typeof apiKeys[0]) => {
    setKeyName(k.client_name);
    setKeyOrigins((k.allowed_origins || []).join("\n"));
    setKeyGuardrails(k.guardrails);
    setKeyIsNew(false); setKeyEditId(k.id); setKeyModalOpen(true);
  };

  const handleSaveKey = async () => {
    const origins = keyOrigins.split("\n").map((s) => s.trim()).filter(Boolean);
    if (keyIsNew) {
      const result = await api.createApiKey(keyName, origins, keyGuardrails);
      setSecretDialog({ keyId: result.key_id, secret: result.secret });
    } else if (keyEditId !== null) {
      await api.updateApiKey(keyEditId, { client_name: keyName, allowed_origins: origins, guardrails: keyGuardrails });
    }
    setKeyModalOpen(false);
    setApiKeys(await api.listApiKeys());
  };

  const handleDeleteKey = async (k: typeof apiKeys[0]) => {
    if (!confirm(t("admin.apiKeys.deleteConfirm", { name: k.client_name }))) return;
    await api.deleteApiKey(k.id);
    setApiKeys(apiKeys.filter((x) => x.id !== k.id));
  };

  const handleRegenerateKey = async (k: typeof apiKeys[0]) => {
    if (!confirm(t("admin.apiKeys.regenerateConfirm", { name: k.client_name }))) return;
    const result = await api.regenerateApiKey(k.id);
    setSecretDialog({ keyId: k.key_id, secret: result.secret });
  };

  const handleToggleActive = async (k: typeof apiKeys[0]) => {
    await api.updateApiKey(k.id, { active: !k.active });
    setApiKeys(await api.listApiKeys());
  };

  // Widgets handlers
  const uiAgents = useMemo(() => agents.filter((a) => a.ui_enabled), [agents]);

  // Agentes ASIGNABLES a un usuario: los interactivos (selector de chat) y tambien
  // los HITL autonomos (ui_enabled=false) — el usuario necesita el permiso para ver
  // y atender sus aprobaciones en la bandeja, aunque no aparezcan en el chat.
  const assignableAgents = useMemo(
    () => agents.filter((a) => a.ui_enabled || a.hitl),
    [agents],
  );

  // La busqueda es la MISMA caja en las dos vistas: un email escrito en "por
  // usuario" sigue filtrando al pasar a "por agente", y al reves.
  const filasPorAgente = useMemo(
    () =>
      usuariosPorAgente(
        users,
        assignableAgents,
        new Set(agents.map((a) => a.graph_id)),
        search,
        (id) => t(`agents.${id}.label`, id),
      ),
    [users, assignableAgents, agents, search, t],
  );

  const resetWidgetForm = useCallback(() => {
    setWidgetForm({ name: "", agent_id: "", context_schema: [], context_prompt_template: "", thread_ttl_hours: 24, allowed_origins: "", auto_start: false });
  }, []);

  const openCreateWidget = () => {
    resetWidgetForm();
    setEditingWidget(null);
    setShowWidgetModal(true);
  };

  const openEditWidget = (w: Widget) => {
    setEditingWidget(w);
    setWidgetForm({
      name: w.name,
      agent_id: w.agent_id,
      context_schema: [...w.context_schema],
      context_prompt_template: w.context_prompt_template || "",
      thread_ttl_hours: w.thread_ttl_hours,
      allowed_origins: (w.allowed_origins || []).join("\n"),
      auto_start: w.auto_start === true,
    });
    setShowWidgetModal(true);
  };

  const handleWidgetAgentChange = (agentId: string) => {
    setWidgetForm((prev) => {
      const agent = agents.find((a) => a.graph_id === agentId);
      if (agent && !editingWidget && agent.context_fields && agent.context_fields.length > 0) {
        return {
          ...prev,
          agent_id: agentId,
          context_schema: [...agent.context_fields],
          context_prompt_template: agent.context_prompt_template || "",
        };
      }
      return { ...prev, agent_id: agentId };
    });
  };

  const handleSaveWidget = async () => {
    const origins = widgetForm.allowed_origins.split("\n").map((s) => s.trim()).filter(Boolean);
    if (editingWidget) {
      await api.updateWidget(editingWidget.id, {
        name: widgetForm.name,
        context_schema: widgetForm.context_schema,
        context_prompt_template: widgetForm.context_prompt_template || undefined,
        thread_ttl_hours: widgetForm.thread_ttl_hours,
        allowed_origins: origins,
        auto_start: widgetForm.auto_start,
      });
    } else {
      const result = await api.createWidget({
        name: widgetForm.name,
        agent_id: widgetForm.agent_id,
        context_schema: widgetForm.context_schema,
        context_prompt_template: widgetForm.context_prompt_template || undefined,
        thread_ttl_hours: widgetForm.thread_ttl_hours,
        allowed_origins: origins,
        auto_start: widgetForm.auto_start,
      });
      setSnippetHtml(result.snippet);
      setLinkUrl(result.link_url);
      setShowSnippetModal(true);
    }
    setShowWidgetModal(false);
    setWidgets(await api.listWidgets());
  };

  const handleDeleteWidget = async (w: Widget) => {
    if (!confirm(t("admin.widgets.confirm_delete", { name: w.name }))) return;
    await api.deleteWidget(w.id);
    setWidgets(widgets.filter((x) => x.id !== w.id));
  };

  const handleToggleWidgetActive = async (w: Widget) => {
    await api.updateWidget(w.id, { active: !w.active });
    setWidgets(await api.listWidgets());
  };

  const handleShowSnippet = async (w: Widget) => {
    const result = await api.getWidgetSnippet(w.id);
    setSnippetHtml(result.html);
    setLinkUrl(result.link_url);
    setShowSnippetModal(true);
  };

  const addContextVariable = () => {
    setWidgetForm((prev) => ({
      ...prev,
      context_schema: [...prev.context_schema, { key: "", label: "", type: "string", required: false }],
    }));
  };

  const removeContextVariable = (index: number) => {
    setWidgetForm((prev) => ({
      ...prev,
      context_schema: prev.context_schema.filter((_, i) => i !== index),
    }));
  };

  const updateContextVariable = (index: number, field: keyof ContextField, value: string | boolean) => {
    setWidgetForm((prev) => ({
      ...prev,
      context_schema: prev.context_schema.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    }));
  };

  // Template validation
  const templateWarnings = useMemo(() => {
    const template = widgetForm.context_prompt_template;
    const schemaKeys = new Set(widgetForm.context_schema.map((v) => v.key).filter(Boolean));
    const templateKeys = new Set<string>();
    const re = /\{\{(\w+)\}\}/g;
    let match;
    while ((match = re.exec(template)) !== null) {
      templateKeys.add(match[1]);
    }
    const unusedVars = [...schemaKeys].filter((k) => !templateKeys.has(k));
    const orphanPlaceholders = [...templateKeys].filter((k) => !schemaKeys.has(k));
    return { unusedVars, orphanPlaceholders };
  }, [widgetForm.context_schema, widgetForm.context_prompt_template]);

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const maskKeyId = (keyId: string) => {
    if (keyId.length <= 8) return keyId;
    return keyId.slice(0, 6) + "..." + keyId.slice(-4);
  };

  const filteredUsers = filterUsers(users, search);
  const totalPages = computeTotalPages(filteredUsers.length);
  const pageUsers = pageSlice(filteredUsers, page);
  const allOnPageSelected = pageUsers.length > 0 && pageUsers.every((u) => selected.has(u.email));
  const someOnPageSelected = pageUsers.some((u) => selected.has(u.email));

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  return (
    <div className="flex h-screen flex-col bg-[var(--background)] text-[var(--foreground)]">
      <Header />
      <div className="flex-1 overflow-auto p-6">
        {/* Ancho UNICO para toda la pagina, y ancho de TABLA, no de lectura.
            Nacio en `max-w-3xl` (768 px) en 2026-03, cuando esto solo era la
            tabla de permisos: esa escala de Tailwind esta calibrada para texto
            (45-75 caracteres por linea) y aqui no hay texto que leer — las
            cuatro pestañas son tablas de cuatro o cinco columnas, y la de
            modelos lleva ademas un grafo de mas de 1000 px cuya columna de
            asignar no cabia.
            Se probo ensanchar SOLO la pestaña de modelos y se descarto al
            verlo: la pagina daba un salto de ancho al cambiar de pestaña, y esa
            sacudida molesta mas que el hueco que ahorraba. Un contenedor que
            cambia de tamaño segun lo que enseña se lee como un fallo. */}
        <div className="mx-auto max-w-6xl">
          <button
            onClick={() => navigate("/")}
            className="mb-4 flex items-center gap-1 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
          >
            <ArrowLeft size={14} />
            {t("common.back")}
          </button>

          <h1 className="mb-1 text-xl font-semibold">{t("admin.title")}</h1>

          <Pestanas activa={pestana} onChange={setPestana} />

          {/* Permissions section */}
          {pestana === "permissions" && (
            <section role="tabpanel" id={idPanel("permissions")} aria-labelledby={idPestana("permissions")} className="mt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-medium">{t("admin.permissions.title")}</h2>
                  <p className="text-sm text-[var(--muted-foreground)]">
                    {t("admin.permissions.description")}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Mismo patron que "Grafo | Tabla" del panel de modelos:
                      radiogroup porque son dos alternativas EXCLUYENTES. */}
                  <div
                    role="radiogroup"
                    aria-label={t("admin.permissions.porAgente.vistaAria")}
                    className="flex shrink-0 rounded-lg border border-[var(--border)] p-0.5"
                  >
                    {VISTAS_PERMISOS.map((v) => (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={vistaPermisos === v}
                        onClick={() => setVistaPermisos(v)}
                        className={`rounded-md px-3 py-1 text-sm ${
                          vistaPermisos === v
                            ? "bg-[var(--muted)] font-medium"
                            : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                        }`}
                      >
                        {v === "usuarios"
                          ? t("admin.permissions.porAgente.vistaUsuarios")
                          : t("admin.permissions.porAgente.vistaAgentes")}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={openBulkModal}
                    className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
                  >
                    <Users size={14} />
                    {t("admin.permissions.bulk.title")}
                  </button>
                  <button
                    onClick={openAdd}
                    className="flex items-center gap-1 rounded-lg bg-[var(--foreground)] px-3 py-1.5 text-sm text-[var(--background)] hover:opacity-90"
                  >
                    <Plus size={14} />
                    {t("admin.permissions.addUser")}
                  </button>
                </div>
              </div>

              {(users.length > 0 || vistaPermisos === "agentes") && (
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      vistaPermisos === "agentes"
                        ? t("admin.permissions.porAgente.searchPlaceholder")
                        : t("admin.permissions.searchPlaceholder")
                    }
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] py-1.5 pl-9 pr-3 text-sm"
                  />
                </div>
              )}

              {vistaPermisos === "usuarios" && selected.size > 0 && (
                <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--muted)] px-4 py-2 text-sm">
                  <span>{t("admin.permissions.bulk.selectedCount", { count: selected.size })}</span>
                  {selected.size < filteredUsers.length && (
                    <button onClick={() => setSelected(selectAll(filteredUsers.map((u) => u.email)))} className="underline">
                      {t("admin.permissions.bulk.selectAllFiltered", { count: filteredUsers.length })}
                    </button>
                  )}
                  <select value={bulkAgent} onChange={(e) => setBulkAgent(e.target.value)} className="rounded border border-[var(--border)] bg-[var(--background)] px-2 py-1">
                    <option value="">{t("admin.permissions.bulk.selectAgents")}</option>
                    {assignableAgents.map((a) => (
                      <option key={a.graph_id} value={a.graph_id}>{t(`agents.${a.graph_id}.label`, a.graph_id)}</option>
                    ))}
                  </select>
                  <button disabled={!bulkAgent} onClick={() => handleBulkSelected("add")} className="rounded bg-[var(--foreground)] px-2 py-1 text-[var(--background)] disabled:opacity-40">{t("admin.permissions.bulk.addToSelected")}</button>
                  <button disabled={!bulkAgent} onClick={() => handleBulkSelected("remove")} className="rounded border border-[var(--border)] px-2 py-1 disabled:opacity-40">{t("admin.permissions.bulk.removeFromSelected")}</button>
                  <button onClick={() => setSelected(new Set())} className="underline">{t("admin.permissions.bulk.clearSelection")}</button>
                </div>
              )}

              {vistaPermisos === "agentes" ? (
                <PermisosPorAgente
                  filas={filasPorAgente}
                  abiertos={agentesAbiertos}
                  onAlternar={(id) => setAgentesAbiertos((prev) => toggleInSet(prev, id))}
                  bulkAssign={api.bulkAssignUsers}
                  onCambio={async () => setUsers(await api.listUsers())}
                />
              ) : users.length === 0 ? (
                <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
                  {t("admin.permissions.noUsers")}
                </p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-[var(--border)]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                        <th className="w-10 px-4 py-2 text-left">
                          <input
                            type="checkbox"
                            className="rounded"
                            ref={(el) => { if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected; }}
                            checked={allOnPageSelected}
                            onChange={toggleSelectAllPage}
                          />
                        </th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.permissions.email")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.permissions.agents")}</th>
                        <th className="w-24 px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {pageUsers.map((u) => (
                        <tr key={u.email} className="border-b border-[var(--border)] last:border-0">
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={selected.has(u.email)}
                              onChange={() => toggleSelected(u.email)}
                            />
                          </td>
                          <td className="px-4 py-2">{u.email}</td>
                          <td className="px-4 py-2">
                            {u.allowed_agents.length === 0 ? (
                              <span className="text-[var(--muted-foreground)]">
                                {t("admin.permissions.noAgents")}
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {u.allowed_agents.map((a) => (
                                  <span
                                    key={a}
                                    className={`rounded-full px-2 py-0.5 text-xs ${agentChipClass(a)}`}
                                  >
                                    {t(`agents.${a}.label`, a)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => openEdit(u)}
                                className="rounded p-1 hover:bg-[var(--muted)]"
                                title={t("admin.permissions.editUser")}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(u.email)}
                                className="rounded p-1 hover:bg-[var(--muted)]"
                                title={t("admin.permissions.deleteUser")}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-2 text-sm">
                      <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded px-2 py-1 disabled:opacity-40 hover:bg-[var(--muted)]">{t("admin.permissions.pagination.prev")}</button>
                      <span>{t("admin.permissions.pagination.page", { page, total: totalPages })}</span>
                      <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="rounded px-2 py-1 disabled:opacity-40 hover:bg-[var(--muted)]">{t("admin.permissions.pagination.next")}</button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* API Keys section */}
          {pestana === "apiKeys" && (
            <section role="tabpanel" id={idPanel("apiKeys")} aria-labelledby={idPestana("apiKeys")} className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium">{t("admin.apiKeys.title")}</h2>
                  <p className="text-sm text-[var(--muted-foreground)]">
                    {t("admin.apiKeys.description")}
                  </p>
                </div>
                <button
                  onClick={openCreateKey}
                  className="flex items-center gap-1 rounded-lg bg-[var(--foreground)] px-3 py-1.5 text-sm text-[var(--background)] hover:opacity-90"
                >
                  <Plus size={14} />
                  {t("admin.apiKeys.create")}
                </button>
              </div>

              {apiKeys.length === 0 ? (
                <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
                  {t("admin.apiKeys.noKeys")}
                </p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-[var(--border)]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                        <th className="px-4 py-2 text-left font-medium">{t("admin.apiKeys.name")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.apiKeys.keyId")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.active")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.apiKeys.lastUsed")}</th>
                        <th className="w-28 px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {apiKeys.map((k) => (
                        <tr key={k.id} className="border-b border-[var(--border)] last:border-0">
                          <td className="px-4 py-2 font-medium">{k.client_name}</td>
                          <td className="px-4 py-2 font-mono text-xs">{maskKeyId(k.key_id)}</td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleToggleActive(k)}
                              className={`rounded-full px-2 py-0.5 text-xs ${k.active ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"}`}
                            >
                              {k.active ? t("admin.active") : t("admin.inactive")}
                            </button>
                          </td>
                          <td className="px-4 py-2 text-xs text-[var(--muted-foreground)]">
                            {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : t("admin.apiKeys.never")}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => openEditKey(k)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.apiKeys.edit")}>
                                <Pencil size={14} />
                              </button>
                              <button onClick={() => handleRegenerateKey(k)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.apiKeys.regenerate")}>
                                <RefreshCw size={14} />
                              </button>
                              <button onClick={() => handleDeleteKey(k)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.apiKeys.delete")}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Widgets section */}
          {pestana === "widgets" && (
            <section role="tabpanel" id={idPanel("widgets")} aria-labelledby={idPestana("widgets")} className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium">{t("admin.widgets.title")}</h2>
                  <p className="text-sm text-[var(--muted-foreground)]">
                    {t("admin.widgets.description")}
                  </p>
                </div>
                <button
                  onClick={openCreateWidget}
                  className="flex items-center gap-1 rounded-lg bg-[var(--foreground)] px-3 py-1.5 text-sm text-[var(--background)] hover:opacity-90"
                >
                  <Plus size={14} />
                  {t("admin.widgets.create")}
                </button>
              </div>

              {widgets.length === 0 ? (
                <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
                  {t("admin.widgets.no_widgets")}
                </p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-[var(--border)]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                        <th className="px-4 py-2 text-left font-medium">{t("admin.widgets.name")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.widgets.agent")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.active")}</th>
                        <th className="px-4 py-2 text-left font-medium">{t("admin.widgets.created")}</th>
                        <th className="w-32 px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {widgets.map((w) => (
                        <tr key={w.id} className="border-b border-[var(--border)] last:border-0">
                          <td className="px-4 py-2 font-medium">{w.name}</td>
                          <td className="px-4 py-2">{w.agent_id}</td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleToggleWidgetActive(w)}
                              className={`rounded-full px-2 py-0.5 text-xs ${w.active ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"}`}
                            >
                              {w.active ? t("admin.active") : t("admin.inactive")}
                            </button>
                          </td>
                          <td className="px-4 py-2 text-xs text-[var(--muted-foreground)]">
                            {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => openEditWidget(w)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.widgets.edit")}>
                                <Pencil size={14} />
                              </button>
                              <button onClick={() => handleShowSnippet(w)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.widgets.snippet")}>
                                <Code size={14} />
                              </button>
                              <button onClick={() => handleDeleteWidget(w)} className="rounded p-1 hover:bg-[var(--muted)]" title={t("admin.widgets.delete")}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Modelos LLM. A diferencia de las otras tres secciones, esta vive en
              su propio componente y gestiona su propio estado: no hay nada que
              el admin pueda estar escribiendo y perder al cambiar de pestana, y
              AdminPage.tsx no necesita crecer otras 300 lineas. */}
          {pestana === "modelos" && (
            <section role="tabpanel" id={idPanel("modelos")} aria-labelledby={idPestana("modelos")} className="mt-6">
              <SeccionModelos api={api.modelos} />
            </section>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-medium">
              {isNew ? t("admin.permissions.addUser") : t("admin.permissions.editUser")}
            </h3>

            <label className="mb-1 block text-sm font-medium">{t("admin.permissions.email")}</label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              disabled={!isNew}
              placeholder={t("admin.permissions.emailPlaceholder")}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm disabled:opacity-50"
            />

            <label className="mb-2 block text-sm font-medium">{t("admin.permissions.agents")}</label>
            <div className="mb-6 space-y-2">
              {assignableAgents.map((a) => (
                <label key={a.graph_id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editAgents.includes(a.graph_id)}
                    onChange={() => toggleAgent(a.graph_id)}
                    className="mt-0.5 rounded"
                  />
                  <div>
                    <span className="font-medium">{t(`agents.${a.graph_id}.label`, a.graph_id)}</span>
                    <p className="text-xs text-[var(--muted-foreground)]">{t(`agents.${a.graph_id}.description`, a.description)}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                {t("admin.permissions.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={!editEmail}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
              >
                {t("admin.permissions.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-by-emails Modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-medium">{t("admin.permissions.bulk.title")}</h3>

            <label className="mb-1 block text-sm font-medium">{t("admin.permissions.bulk.emailsLabel")}</label>
            <textarea
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              placeholder={t("admin.permissions.bulk.emailsPlaceholder")}
              rows={5}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />

            <label className="mb-2 block text-sm font-medium">{t("admin.permissions.bulk.selectAgents")}</label>
            <div className="mb-4 space-y-2">
              {assignableAgents.map((a) => (
                <label key={a.graph_id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={bulkModalAgents.includes(a.graph_id)}
                    onChange={() => toggleBulkModalAgent(a.graph_id)}
                    className="mt-0.5 rounded"
                  />
                  <div>
                    <span className="font-medium">{t(`agents.${a.graph_id}.label`, a.graph_id)}</span>
                    <p className="text-xs text-[var(--muted-foreground)]">{t(`agents.${a.graph_id}.description`, a.description)}</p>
                  </div>
                </label>
              ))}
            </div>

            {bulkResult && (
              <div className="mb-4 rounded-lg border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-sm">
                <p>{t("admin.permissions.bulk.result", { count: bulkResult.updated })}</p>
                {bulkResult.invalid.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-amber-600">{t("admin.permissions.bulk.invalidTitle")}</p>
                    <ul className="mt-1 list-inside list-disc text-xs text-[var(--muted-foreground)]">
                      {bulkResult.invalid.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBulkModalOpen(false)}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                {t("admin.permissions.bulk.close")}
              </button>
              <button
                onClick={handleBulkByEmails}
                disabled={!bulkEmails.trim() || bulkModalAgents.length === 0}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
              >
                {t("admin.permissions.bulk.assign")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {keyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-medium">
              {keyIsNew ? t("admin.apiKeys.create") : t("admin.apiKeys.edit")}
            </h3>

            <label className="mb-1 block text-sm font-medium">{t("admin.apiKeys.name")}</label>
            <input
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder={t("admin.apiKeys.namePlaceholder")}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />

            <label className="mb-1 block text-sm font-medium">{t("admin.apiKeys.origins")}</label>
            <textarea
              value={keyOrigins}
              onChange={(e) => setKeyOrigins(e.target.value)}
              placeholder={t("admin.apiKeys.originsPlaceholder")}
              rows={3}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />

            <label className="mb-6 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={keyGuardrails}
                onChange={(e) => setKeyGuardrails(e.target.checked)}
                className="rounded"
              />
              {t("admin.apiKeys.guardrails")}
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setKeyModalOpen(false)}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                {t("admin.apiKeys.cancel")}
              </button>
              <button
                onClick={handleSaveKey}
                disabled={!keyName}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
              >
                {t("admin.apiKeys.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Widget Modal */}
      {showWidgetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-medium">
              {editingWidget ? t("admin.widgets.edit") : t("admin.widgets.create")}
            </h3>

            <label className="mb-1 block text-sm font-medium">{t("admin.widgets.name")}</label>
            <input
              type="text"
              value={widgetForm.name}
              onChange={(e) => setWidgetForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t("admin.widgets.namePlaceholder")}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />

            <label className="mb-1 block text-sm font-medium">{t("admin.widgets.agent")}</label>
            <select
              value={widgetForm.agent_id}
              onChange={(e) => handleWidgetAgentChange(e.target.value)}
              disabled={!!editingWidget}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="">{t("admin.widgets.selectAgent")}</option>
              {uiAgents.map((a) => (
                <option key={a.graph_id} value={a.graph_id}>
                  {a.graph_id} — {a.description}
                </option>
              ))}
            </select>

            <label className="mb-1 block text-sm font-medium">{t("admin.widgets.ttl")}</label>
            <input
              type="number"
              value={widgetForm.thread_ttl_hours}
              onChange={(e) => setWidgetForm((prev) => ({ ...prev, thread_ttl_hours: Number(e.target.value) || 24 }))}
              min={1}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />

            <label className="mb-1 flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={widgetForm.auto_start}
                onChange={(e) => setWidgetForm((prev) => ({ ...prev, auto_start: e.target.checked }))}
                className="rounded"
              />
              {t("admin.widgets.autoStart")}
            </label>
            <p className="mb-4 text-xs text-[var(--muted-foreground)]">{t("admin.widgets.autoStartHelp")}</p>

            <label className="mb-1 block text-sm font-medium">{t("admin.widgets.origins")}</label>
            <p className="mb-1 text-xs text-[var(--muted-foreground)]">{t("admin.widgets.origins_help")}</p>
            <textarea
              value={widgetForm.allowed_origins}
              onChange={(e) => setWidgetForm((prev) => ({ ...prev, allowed_origins: e.target.value }))}
              rows={3}
              className="mb-2 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />
            {!widgetForm.allowed_origins.trim() && (
              <p className="mb-4 flex items-center gap-1.5 text-xs text-amber-600">
                <span title={t("admin.widgets.origins_warning")}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                </span>
                {t("admin.widgets.origins_warning")}
              </p>
            )}

            <label className="mb-2 block text-sm font-medium">{t("admin.widgets.context_schema")}</label>
            {widgetForm.context_schema.length > 0 && (
              <div className="mb-2 overflow-hidden rounded-lg border border-[var(--border)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                      <th className="px-2 py-1.5 text-left text-xs font-medium">{t("admin.widgets.context_key")}</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium">{t("admin.widgets.context_label")}</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium">{t("admin.widgets.context_type")}</th>
                      <th className="px-2 py-1.5 text-center text-xs font-medium">{t("admin.widgets.context_required")}</th>
                      <th className="w-16 px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {widgetForm.context_schema.map((v, i) => (
                      <tr key={i} className="border-b border-[var(--border)] last:border-0">
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={v.key}
                              onChange={(e) => updateContextVariable(i, "key", e.target.value)}
                              className="w-full rounded border border-[var(--border)] bg-[var(--background)] px-1.5 py-1 text-xs"
                            />
                            {v.key && templateWarnings.unusedVars.includes(v.key) && (
                              <span title={t("admin.widgets.template_warning_unused")}><AlertTriangle size={12} className="shrink-0 text-amber-500" /></span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={v.label}
                            onChange={(e) => updateContextVariable(i, "label", e.target.value)}
                            className="w-full rounded border border-[var(--border)] bg-[var(--background)] px-1.5 py-1 text-xs"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <select
                            value={v.type}
                            onChange={(e) => updateContextVariable(i, "type", e.target.value)}
                            className="w-full rounded border border-[var(--border)] bg-[var(--background)] px-1.5 py-1 text-xs"
                          >
                            <option value="string">string</option>
                            <option value="text">text</option>
                          </select>
                        </td>
                        <td className="px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={v.required}
                            onChange={(e) => updateContextVariable(i, "required", e.target.checked)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => removeContextVariable(i)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            {t("admin.widgets.remove_variable")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={addContextVariable}
              className="mb-4 flex items-center gap-1 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
            >
              <Plus size={12} />
              {t("admin.widgets.add_variable")}
            </button>

            <label className="mb-1 block text-sm font-medium">{t("admin.widgets.context_prompt")}</label>
            <textarea
              value={widgetForm.context_prompt_template}
              onChange={(e) => setWidgetForm((prev) => ({ ...prev, context_prompt_template: e.target.value }))}
              rows={5}
              className="mb-1 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 font-mono text-sm"
            />
            {templateWarnings.orphanPlaceholders.length > 0 && (
              <p className="mb-4 flex items-center gap-1 text-xs text-red-500">
                <AlertTriangle size={12} />
                {t("admin.widgets.template_warning_orphan")}: {templateWarnings.orphanPlaceholders.map((k) => `{{${k}}}`).join(", ")}
              </p>
            )}
            {templateWarnings.orphanPlaceholders.length === 0 && <div className="mb-4" />}

            {editingWidget && (
              <label className="mb-6 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editingWidget.active}
                  onChange={() => {
                    setEditingWidget({ ...editingWidget, active: !editingWidget.active });
                    handleToggleWidgetActive(editingWidget);
                  }}
                  className="rounded"
                />
                {t("admin.widgets.active")}
              </label>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowWidgetModal(false)}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                {t("admin.widgets.cancel")}
              </button>
              <button
                onClick={handleSaveWidget}
                disabled={!widgetForm.name || !widgetForm.agent_id}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
              >
                {t("admin.widgets.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snippet Modal */}
      {showSnippetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-medium">{t("admin.widgets.snippet_title")}</h3>
            <pre className="mb-4 max-h-96 overflow-auto rounded-lg bg-zinc-900 p-4 font-mono text-xs leading-relaxed text-green-400"><code>{snippetHtml}</code></pre>
            <div className="mb-4 flex justify-end">
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(snippetHtml);
                  setSnippetCopied(true);
                  setTimeout(() => setSnippetCopied(false), 2000);
                }}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                {snippetCopied ? t("admin.widgets.snippet_copied") : t("admin.widgets.snippet_copy")}
              </button>
            </div>
            {linkUrl && (
              <>
                <h4 className="mb-2 mt-4 text-sm font-medium">{t("admin.widgets.link_title")}</h4>
                <pre className="mb-4 overflow-auto rounded-lg bg-zinc-900 p-4 font-mono text-xs leading-relaxed text-blue-400">
                  <code>{linkUrl}</code>
                </pre>
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={() => copyToClipboard(linkUrl, "link")}
                    className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)]"
                  >
                    {copied === "link" ? t("admin.widgets.snippet_copied") : t("admin.widgets.link_copy")}
                  </button>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSnippetModal(false)}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90"
              >
                {t("admin.widgets.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret reveal dialog */}
      {secretDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-medium">{t("admin.apiKeys.secretTitle")}</h3>
            <p className="mb-4 text-sm text-amber-600 dark:text-amber-400">
              {t("admin.apiKeys.secretWarning")}
            </p>

            <div className="mb-6 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]">{t("admin.apiKeys.keyId")}</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-[var(--muted)] px-3 py-2 font-mono text-sm">{secretDialog.keyId}</code>
                  <button
                    onClick={() => copyToClipboard(secretDialog.keyId, "keyId")}
                    className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs hover:bg-[var(--muted)]"
                  >
                    {copied === "keyId" ? t("admin.apiKeys.copied") : t("admin.apiKeys.copyKeyId")}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]">Secret</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-[var(--muted)] px-3 py-2 font-mono text-sm">{secretDialog.secret}</code>
                  <button
                    onClick={() => copyToClipboard(secretDialog.secret, "secret")}
                    className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs hover:bg-[var(--muted)]"
                  >
                    {copied === "secret" ? t("admin.apiKeys.copied") : t("admin.apiKeys.copySecret")}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]">Header</label>
                <code className="block break-all rounded bg-[var(--muted)] px-3 py-2 font-mono text-sm">
                  X-API-Key: {secretDialog.keyId}:{secretDialog.secret}
                </code>
              </div>
            </div>

            <button
              onClick={() => setSecretDialog(null)}
              className="w-full rounded-lg bg-[var(--foreground)] px-4 py-2 text-sm text-[var(--background)] hover:opacity-90"
            >
              {t("admin.apiKeys.understood")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
